import { LANGUAGES } from "translator-tools";

import _ from "underscore";
import DriveView from "../../DriveView";
import Api from "../../services/api";
import toastr from "../../utils/toastr";
import Spinner from "../../utils/spinner";
import { NetworkStates, PageLevel } from "../../store/mobile/types";

import pageHTML from "./languageChange.tmpl";
import SelectList, { ISelectListOptions } from "../framework/selectList";
import { createElement } from "../../utils/vanilla";
import { handleApi } from "../../utils/error";
import { getUser, setUser } from "./helpers";
import { dispatcher, DispatcherEvents } from "../../utils/dispatcher";

export default class ChangeLanguageView extends DriveView<Backbone.Model> {

    protected strings = [
        "Language changed",
        "You cannot change your language while offline."
    ];
    private readonly api: Api;
    private readonly currentUser: DeepReadonlyObject<IUser>;
    private readonly template = _.template(pageHTML);
    private readonly waiting = new Spinner();
    private readonly languagesHtml: HTMLDivElement;
    private selectedLanguageId: string;

    constructor(options) {
        super(options, {
            title: "Language",
            activeSection: "settings",
            pageLevel: PageLevel.TWO
        });
        const canEditSettings = this.app.securityService.can("editUser");
        if (!canEditSettings) {
            // silently redirect to settings
            this.app.router.navigate("settings/main", true);
        }

        const user = this.app.userService.getCurrentUser();
        if (!user) {
            this.createErrorDialog("Failed to load current user");
            this.app.router.navigate("settings/main", true);
        }
        this.api = this.app.api;
        this.currentUser = user as DeepReadonlyObject<IUser>;
        this.languagesHtml = createElement("div", undefined, "languages-container");
        this.createLanguagesSelectList();
    }

    /* eslint-disable @typescript-eslint/unbound-method */
    events() {
        return {
            "click #submitChange": this.submitChangeChange
        } as Backbone.EventsHash;
    }

    createLanguagesSelectList() {
        this.getLanguageListView(this.languagesHtml, LANGUAGES.map(language => {
            return {
                id: language.value,
                name: language.label,
                isSelected: this.selectedLanguageId ? this.selectedLanguageId === language.value : this.currentUser?.language === language.value
            };
        }));
    }

    render() {
        void this.display(this.template()).then(() => {
            let listElement = (document.getElementById("languages-list") as HTMLElement);
            if (listElement) {
                listElement.appendChild(this.languagesHtml);
            }
        });
        return this;
    }

    onDone() {
        this.stopProgress();
        toastr.success(this.getString(this.strings[0]));
        this.app.router.navigate("settings/main", true);
    }

    createErrorDialog(errorMessage) {
        toastr.error(
            this.getString(errorMessage),
            undefined,
            undefined,
            {
                isUserNightMode: this.app.store.getState().data.nightMode,
                toastId: "language_change_error",
                origin: "language_timezone_changene"
            }
        );
    }

    private getLanguageListView(rulesetHtml: HTMLDivElement, selectableLanguages: ISelectListOptions[]) {
        let selectList = new SelectList({
            el: rulesetHtml,
            selectCallback: item => {
                if (item) {
                    item.isSelected = true;
                    this.selectedLanguageId = item.id;
                    this.createLanguagesSelectList();
                }
            },
            title: "languages-list"
        });
        selectList.render(selectableLanguages);
    }

    private startProgress() {
        let submitButton = <HTMLButtonElement> this.el.querySelector("#submitChange");
        submitButton && submitButton.setAttribute("disabled", "disabled");
        submitButton && this.waiting.spin(submitButton);
    }

    private stopProgress() {
        let submitChangeButton = <HTMLButtonElement> this.el.querySelector("#submitChange");
        submitChangeButton && submitChangeButton.removeAttribute("disabled");
        submitChangeButton && this.waiting.stop();
    }

    private submitChangeChange() {
        this.startProgress();
        if (!this.selectedLanguageId) {
            this.createErrorDialog("Select a language to continue.");
            this.stopProgress();
            return;
        }

        if (this.app.store.getState().mobile.online !== NetworkStates.ONLINE) {
            this.createErrorDialog(this.getString(this.strings[1]));
            this.stopProgress();
            return;
        }

        getUser(this.api, this.currentUser.id).then(user => {
            const updatedUser = {
                ...user,
                language: this.selectedLanguageId
            };
            setUser(this.api, updatedUser).then(() => {
                this.app.userService.setUser(updatedUser);
                dispatcher.trigger(DispatcherEvents.UserUpdate, updatedUser);
                this.onDone();
            }).catch(error => {
                this.stopProgress();
                if (error) {
                    this.createErrorDialog(error.message);
                    handleApi(error);
                }
            });
        }).catch(error => {
            this.stopProgress();
            if (error) {
                this.createErrorDialog("We could not retrieve user information, please try again.");
                handleApi(error);
            }
        });
    }
}